import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useDeviceDetection } from '../../../hooks/UseDeviceDetection';
import { useDependency } from '../../../contexts/DependencyProvider';
import { useViewInfo } from '../../../hooks/UseViewInfo';
import useScreenSize from '../../../hooks/UseScreenSize';
import moment from 'moment';
import {
  Flex,
  View,
  Grid,
  TableView,
  TableBody,
  TableHeader,
  Row,
  Cell,
  Column,
  Heading,
  Content,
  ListBox,
  Item,
  IllustratedMessage,
  Link,
  DialogContainer,
} from '@adobe/react-spectrum';
import { CircleFill } from 'react-bootstrap-icons';
import { FinderIcon } from '../../../components/protected/Icons/IconsLib';
import NotFound from '@spectrum-icons/illustrations/NotFound';
import FilterComponent from './component/FilterComponent';
import { ProjectActivitiesLayout } from './components/ProjectActivitiesLayout';
import { MobileListView } from './components/MobileListView';
import { MobileDetailView } from './components/MobileDetailView';
import { Loader } from './components/Loader';
import HeaderComponent from './component/HeaderComponent';
import { invokeFinderApp } from '../../../utils/NavigationUtils';
import { RootState } from '../../../state/store';
import CardComponent from './component/CardComponent';
import CardComponentMobile from './component/CardComponentMobile';
import { ComponentFactory } from '../../../components/protected/layout-components/ComponentFactory';
import { FPAData, FPADataTypes } from '../../../infra/protected/FPA/FPAData';
import { usePreloadAssets } from '../../../hooks/UsePreloadAssets';
import FilterSearchTextBox from './component/FilterSearchTextBox';
import useNumberFormatter from '../../../i18n/useNumberFormatter';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateAppInfo } from '../../../state/userWebLayout/userWebLayoutSlice';
import { DataProvider } from '../../../components/protected/data-components/Contexts/DataContext';
import { ContextNewDialog, ContextNewDialogMode } from '../../../components/protected/Context/ContextNewDialog';
import { addKey, useKeyboardShortcuts } from '../../../hooks/UseKeyboardShortcuts';
import { DeleteConfirmationDialog } from '../finder/components/DeleteConfirmationDialog';
import { DeleteProjectRequest } from '../../../services/soap/project/requests/DeleteProjectRequest';
import { ToastQueue } from '@adobe/react-spectrum';
import { ContextMoveDialog } from '../../../components/protected/Context/ContextMoveDialog';
import { subscribe, unsubscribe } from '../../../infra/events/Events';
import { AtollonEvent } from '../../../infra/events/Events';
import { ListRegisterUtils } from '../../../utils/ListRegisterUtils';
import { ListRegisterRequest } from '../../../services/soap/features/requests/ListRegisterRequest';
import { CreateRegisterRequest } from '../../../services/soap/features/requests/CreateRegisterRequest';
import { UpdateRegisterRequest } from '../../../services/soap/features/requests/UpdateRegisterRequest';
import { useProjectActivitiesState } from './hooks/useProjectActivitiesState';
import { useProjectActivitiesData } from './hooks/useProjectActivitiesData';
import type { ProjectItem, ProjectStatus, ColumnDefinition, QueryParams, ProjectActivitiesDataRef, PageView, FilterColumn } from './types';
import type { LayoutDefinition } from '../../../infra/protected/Layout/LayoutDefinitions';
import type { Selection } from '@react-types/shared';
import type { Row as RegisterRow } from '../../../services/soap/features/responses/ListRegisterResponse';
import { CustomView } from '../../../services/soap/dataObjects/CustomView';
import { applyViewSelection as applyViewSelectionUtil } from '../../../utils/applyViewSelection';
import { mergeColumns } from './utils/columnMergeUtils';
import { CustomViewDialog } from '../../../components/protected/Dialogs/CustomViewDialog';
import { AtollonButton, AtollonButtonDesignType } from '../../../components/ui/Button/AtollonButton';
import { DeleteCustomViewsRequest } from '../../../services/soap/features/requests/DeleteCustomViewsRequest';
import { AlertDialog } from '@adobe/react-spectrum';
import './project_activities.css';
import styles from './project_activities.module.css';

function normalizeQueryParamsForCompare(params: QueryParams): string {
  const cols = (params?.FILTER?.COL ?? [])
    .map(col => ({
      name: String(col?.name ?? ''),
      type: String(col?.type ?? ''),
      value: String(col?.value ?? ''),
    }))
    .sort((a, b) => {
      if (a.name !== b.name) return a.name.localeCompare(b.name);
      if (a.type !== b.type) return a.type.localeCompare(b.type);
      return a.value.localeCompare(b.value);
    });
  const fields = [...(params?.FIELDS?.field ?? [])].map(v => String(v ?? '')).sort((a, b) => a.localeCompare(b));
  const order = [...(params?.ORDER?.COL ?? [])]
    .map(col => ({
      name: String(col?.name ?? ''),
      desc: String(col?.desc ?? ''),
    }))
    .sort((a, b) => {
      if (a.name !== b.name) return a.name.localeCompare(b.name);
      return a.desc.localeCompare(b.desc);
    });

  return JSON.stringify({ cols, fields, order });
}

export function ProjectActivities() {
  const { t } = useTranslation();
  const allStatesLabel = t('all_states', { ns: 'layout_components', defaultValue: 'All states' });
  const screenSize = useScreenSize();
  const { isMobile } = useViewInfo();
  const deviceInfo = useDeviceDetection(isMobile);
  const { activityStatusMapping, currencies, projectStatusMapping } = usePreloadAssets();
  const { store, projectService, featureService } = useDependency();
  const dataStore = useSelector((state: RootState) => state.userWebLayout.appInfos);
  const selectedApp = useSelector((state: RootState) => state.userWebLayout.selectedAppId);
  const dispatch = useDispatch();
  const { state } = useLocation();
  const listBoxRef = useRef<HTMLDivElement | null>(null);
  // Use custom hook for state management
  const { state: componentState, actions } = useProjectActivitiesState();
  // Destructure state for easier access
  const { 
    showLoader, 
    showLoaderDetail, 
    rowsData, 
    pageView, 
    selectedKeys, 
    definition, 
    showMobileDetailView, 
    selectedItem, 
    selectedDataRow, 
    showAdd, 
    isMoveDialogOpen, 
    isDeleteDialogOpen, 
    showFilter 
  } = componentState;
  const searchedValueRef = useRef<string>('');
  const [projectStatusList, setProjectStatusList] = useState<ProjectStatus[]>([]);
  const stopLoadDataRef = useRef<boolean>(false);
  const dataRef = useRef<ProjectActivitiesDataRef>({});
  const formatNumber = useNumberFormatter();
  const modeRef = useRef<ContextNewDialogMode>('new');
  const previousSelectedAppRef = useRef<string | null>(null);

  const dataActions = useMemo(
    () => ({
      appendRowsData: actions.appendRowsData,
      setRowsData: actions.setRowsData,
      setSelectedKeys: actions.setSelectedKeys,
      setSelectedDataRow: actions.setSelectedDataRow,
      setSelectedItem: actions.setSelectedItem,
      setDefinition: (payload: unknown) => actions.setDefinition(payload as LayoutDefinition | null),
      setShowLoader: actions.setShowLoader,
      setShowLoaderDetail: actions.setShowLoaderDetail,
    }),
    [actions]
  );

  const onDataError = useCallback((msg: string) => {
    ToastQueue.negative(msg || 'Error', { timeout: 3000 });
  }, []);

  const data = useProjectActivitiesData({
    selectedApp,
    store,
    projectService,
    featureService,
    currencies,
    formatNumber,
    activityStatusMapping,
    projectStatusList,
    rowsData,
    selectedKeys,
    stopLoadDataRef,
    actions: dataActions,
    onError: onDataError,
  });

  const {
    listProjectList,
    columns: columnsDataRef,
    columnsState,
    projectListKey,
    queryParams,
    reload: dataReload,
    fetchProjectLayout,
    formSettings,
    customViews,
    hasMoreCustomViews,
    loadMoreCustomViews,
    refreshCustomViews,
    isViewsLoading,
  } = data;

  const [initialCustomViewId, setInitialCustomViewId] = useState<string | undefined>(undefined);
  const [lastViewRegisterLoaded, setLastViewRegisterLoaded] = useState<boolean>(false);
  const [isCreateViewDialogOpen, setIsCreateViewDialogOpen] = useState<boolean>(false);
  const [customViewDialogEditView, setCustomViewDialogEditView] = useState<CustomView | null>(null);
  const [customViewDialogIsDuplicate, setCustomViewDialogIsDuplicate] = useState<boolean>(false);
  const [viewToDelete, setViewToDelete] = useState<CustomView | null>(null);
  const [isDeletingView, setIsDeletingView] = useState<boolean>(false);

  const lastViewIdFromRegisterRef = useRef<string | null>(null);
  const lastViewRegisterRowRef = useRef<Pick<RegisterRow, 'id' | 'assignTo'> | null>(null);
  const resolvedInitialViewForAppRef = useRef<string | null>(null);
  const skipNextViewPersistRef = useRef<boolean>(false);
  const lastAppliedCustomViewIdRef = useRef<string | null>(null);
  const persistLastViewRequestIdRef = useRef<number>(0);
  const pendingCreatedViewIdRef = useRef<string | null>(null);
  const pendingViewRetryCountRef = useRef<number>(0);
  const resetViewBaselineRef = useRef<(() => void) | null>(null);

  const getFilterCol = useCallback((params: QueryParams | undefined | null): FilterColumn[] => {
    return params?.FILTER?.COL ?? [];
  }, []);

  const stateFilter = getFilterCol(queryParams.current).find(col => col.name === 'state');
  const activeViewId = stateFilter?.value != null ? String(stateFilter.value) : null;
  const fieldsSignature = Array.isArray(queryParams.current?.FIELDS?.field)
    ? queryParams.current.FIELDS!.field.join('|')
    : '';

  const mergedColumns = useMemo(() => {
    return mergeColumns(columnsState, customViews, queryParams.current);
  }, [columnsState, customViews, fieldsSignature, activeViewId]);

  const activeViewDefinition = useMemo(() => {
    if (!activeViewId) return '';
    return customViews.find(v => String(v.id ?? '') === activeViewId)?.definition ?? '';
  }, [activeViewId, customViews]);

  const visibleColumnKeySet = useMemo(() => {
    if (!activeViewId) return null;
    if (!activeViewDefinition) return null;
    try {
      const parsed = JSON.parse(activeViewDefinition) as {
        columns?: Array<{ itemname?: string; type?: string; isVisible?: boolean }>;
      };
      const cols = Array.isArray(parsed?.columns) ? parsed.columns : [];
      const visibleKeys = new Set<string>();
      cols.forEach(col => {
        if (col?.isVisible !== true) return;
        const rawItemname = String(col?.itemname ?? '').trim();
        if (!rawItemname) return;
        let key = rawItemname;
        if (rawItemname.includes('|')) {
          const [, right] = rawItemname.split('|');
          if (right?.trim()) key = right.trim();
        } else if (col?.type && String(col.type).includes('|')) {
          const [, rightType] = String(col.type).split('|');
          if (rightType?.trim()) key = rightType.trim();
        }
        if (key) visibleKeys.add(key);
      });
      return visibleKeys;
    } catch {
      return null;
    }
  }, [activeViewDefinition, activeViewId]);

  const visibleGridColumns = useMemo(() => {
    if (!activeViewId) return mergedColumns;
    if (!visibleColumnKeySet) return mergedColumns;
    return mergedColumns.filter(c => visibleColumnKeySet.has(c.key));
  }, [activeViewId, mergedColumns, visibleColumnKeySet]);

  const getLayoutOrientationFromCustomView = useCallback((): {
    desktopOrientation?: 'Detail' | 'Full';
    tabletOrientation?: 'Detail' | 'Full';
    mobileOrientation?: 'Detail' | 'Full';
  } | null => {
    const stateFilter = getFilterCol(queryParams.current).find(col => col.name === 'state');
    if (!stateFilter?.value) return null;
    const viewId = String(stateFilter.value);
    const selectedView = customViews.find(v => v.id === viewId);
    if (!selectedView?.definition) return null;
    try {
      const definition = JSON.parse(selectedView.definition);
      const desktopOrientationRaw = definition.desktopOrientation;
      const desktopOrientation: 'Detail' | 'Full' | undefined =
        desktopOrientationRaw === 'table view' || desktopOrientationRaw === 'Detail'
          ? 'Detail'
          : desktopOrientationRaw === 'cards view' || desktopOrientationRaw === 'Full'
            ? 'Full'
            : undefined;
      const mobileOrientationRaw = definition.mobileOrientation;
      const defaultTabletOrientation: 'Detail' | 'Full' | undefined =
        mobileOrientationRaw === 'cards view' || mobileOrientationRaw === 'Full'
          ? 'Full'
          : mobileOrientationRaw === 'table view' || mobileOrientationRaw === 'Detail'
            ? 'Detail'
            : undefined;
      const tabletOrientationRaw =
        definition.tabletOrientation ??
        definition.tabletHorizontalOrientation ??
        definition.tabletVerticalOrientation ??
        mobileOrientationRaw;
      const tabletOrientation: 'Detail' | 'Full' | undefined =
        tabletOrientationRaw === 'table view' || tabletOrientationRaw === 'Detail'
          ? 'Detail'
          : tabletOrientationRaw === 'cards view' || tabletOrientationRaw === 'Full'
            ? 'Full'
            : defaultTabletOrientation;
      return {
        desktopOrientation,
        tabletOrientation,
        mobileOrientation: defaultTabletOrientation,
      };
    } catch {
      return null;
    }
  }, [customViews, getFilterCol, queryParams]);

  const listLastViewRegisterRow = useCallback(async (): Promise<RegisterRow | null> => {
    if (!selectedApp) return null;
    try {
      const res = await featureService.listRegister(
        new ListRegisterRequest(
          store.Server,
          store.SessionId,
          ListRegisterUtils.Modules.Project,
          ListRegisterUtils.Keys.Project.LastViewProjectActivities,
          1,
          ListRegisterUtils.Levels.Project.Default,
          Number(selectedApp)
        )
      );
      if (!res || res.count <= 0 || !Array.isArray(res.ROWS) || res.ROWS.length === 0) return null;
      return res.ROWS[0]?.ROW ?? null;
    } catch (error) {
      console.error('Error loading last view register row:', error);
      return null;
    }
  }, [featureService, selectedApp, store.Server, store.SessionId]);

  const persistLastViewId = useCallback(
    async (viewId: string): Promise<void> => {
      if (!selectedApp) return;
      const normalizedViewId = String(viewId ?? '').trim();
      if (!normalizedViewId) return;

      const requestId = ++persistLastViewRequestIdRef.current;

      try {
        let rowMeta = lastViewRegisterRowRef.current;
        if (!rowMeta) {
          const existingRow = await listLastViewRegisterRow();
          if (requestId !== persistLastViewRequestIdRef.current) return;
          if (existingRow?.id) {
            rowMeta = { id: existingRow.id, assignTo: existingRow.assignTo };
            lastViewRegisterRowRef.current = rowMeta;
          }
        }

        if (rowMeta?.id) {
          await featureService.updateRegister(
            new UpdateRegisterRequest(
              store.Server,
              store.SessionId,
              rowMeta.id,
              undefined,
              ListRegisterUtils.Modules.Project,
              Number(selectedApp),
              ListRegisterUtils.Keys.Project.LastViewProjectActivities,
              ListRegisterUtils.Levels.Project.Default,
              normalizedViewId,
              rowMeta.assignTo
            )
          );
          return;
        }

        await featureService.createRegister(
          new CreateRegisterRequest(
            store.Server,
            store.SessionId,
            ListRegisterUtils.Modules.Project,
            ListRegisterUtils.Keys.Project.LastViewProjectActivities,
            ListRegisterUtils.Levels.Project.Default,
            normalizedViewId,
            String(selectedApp)
          )
        );

        if (requestId !== persistLastViewRequestIdRef.current) return;

        const createdRow = await listLastViewRegisterRow();
        if (createdRow?.id) {
          lastViewRegisterRowRef.current = { id: createdRow.id, assignTo: createdRow.assignTo };
        }
      } catch (error) {
        console.error('Error persisting last view id:', error);
      }
    },
    [featureService, listLastViewRegisterRow, selectedApp, store.Server, store.SessionId]
  );

  const applyViewSelection = useCallback(
    (view: CustomView): QueryParams =>
      applyViewSelectionUtil(view, store) as QueryParams,
    [store]
  );

  const handleReloadProjectActivities = useCallback(
    (filterParams: QueryParams) => {
      const currentStateFilter = getFilterCol(queryParams.current).find(col => col.name === 'state');
      const newStateFilter = getFilterCol(filterParams).find(col => col.name === 'state');
      const currentViewId = currentStateFilter?.value ? String(currentStateFilter.value) : null;
      const newViewId = newStateFilter?.value ? String(newStateFilter.value) : null;
      const isSameQuery = normalizeQueryParamsForCompare(queryParams.current) === normalizeQueryParamsForCompare(filterParams);

      if (isSameQuery) {
        return;
      }

      if (currentViewId !== newViewId) lastAppliedCustomViewIdRef.current = null;
      if (currentViewId !== newViewId && newViewId && !skipNextViewPersistRef.current) {
        void persistLastViewId(newViewId);
      }
      if (skipNextViewPersistRef.current) skipNextViewPersistRef.current = false;

      actions.setSelectedKeys(undefined);
      actions.setSelectedDataRow({});
      actions.setSelectedItem(null);
      actions.setDefinition(null);
      dataRef.current.selectedItem = undefined;
      dataRef.current.selectedRow = undefined;
      dataRef.current.selectedKeys = undefined;
      stopLoadDataRef.current = false;
      actions.setShowLoader(true);
      dataReload(filterParams);
    },
    [actions, dataReload, getFilterCol, persistLastViewId]
  );

  const handleOpenCreateViewDialog = useCallback(() => {
    setCustomViewDialogEditView(null);
    setCustomViewDialogIsDuplicate(false);
    setIsCreateViewDialogOpen(true);
  }, []);

  const handleCloseCustomViewDialog = useCallback(() => {
    setIsCreateViewDialogOpen(false);
    setCustomViewDialogEditView(null);
    setCustomViewDialogIsDuplicate(false);
  }, []);

  const handleCreateViewSaved = useCallback(
    async (_data: unknown, viewId?: string) => {
      const wasEditOrDuplicate = customViewDialogEditView != null;
      setIsCreateViewDialogOpen(false);
      setCustomViewDialogEditView(null);
      setCustomViewDialogIsDuplicate(false);
      if (!viewId) {
        resolvedInitialViewForAppRef.current = null;
        setInitialCustomViewId(undefined);
        await refreshCustomViews();
        if (wasEditOrDuplicate) resetViewBaselineRef.current?.();
        return;
      }
      pendingCreatedViewIdRef.current = String(viewId);
      pendingViewRetryCountRef.current = 0;
      resolvedInitialViewForAppRef.current = null;
      setInitialCustomViewId(undefined);
      await refreshCustomViews();
      if (wasEditOrDuplicate) resetViewBaselineRef.current?.();
    },
    [customViewDialogEditView, refreshCustomViews]
  );

  const handleEditView = useCallback((view: CustomView) => {
    setCustomViewDialogEditView(view);
    setCustomViewDialogIsDuplicate(false);
    setIsCreateViewDialogOpen(true);
  }, []);

  const handleDuplicateView = useCallback((view: CustomView) => {
    setCustomViewDialogEditView({
      ...view,
      id: undefined,
      name: view.name ? `Copy of ${view.name}` : undefined,
    });
    setCustomViewDialogIsDuplicate(true);
    setIsCreateViewDialogOpen(true);
  }, []);

  const handleDeleteViewClick = useCallback((view: CustomView) => {
    setViewToDelete(view);
  }, []);

  const handleDeleteViewConfirm = useCallback(async () => {
    if (!viewToDelete?.id) return;
    const wasSelected =
      getFilterCol(queryParams.current).find(col => col.name === 'state')?.value != null &&
      String(getFilterCol(queryParams.current).find(col => col.name === 'state')?.value) === String(viewToDelete.id);

    setIsDeletingView(true);
    try {
      const response = await featureService.deleteCustomViews(
        new DeleteCustomViewsRequest(store.Server, store.SessionId, [{ id: viewToDelete.id }])
      );
      if (response?.result === 'OK') {
        if (wasSelected) {
          const { FIELDS: _f, ORDER: _o, ...rest } = queryParams.current as QueryParams & { FIELDS?: unknown; ORDER?: unknown };
          const updatedParams: QueryParams = {
            ...rest,
            FILTER: {
              COL: getFilterCol(queryParams.current).filter(col => col.name !== 'state'),
            },
          };
          handleReloadProjectActivities(updatedParams);
        }
        await refreshCustomViews();
      }
    } finally {
      setIsDeletingView(false);
      setViewToDelete(null);
    }
  }, [viewToDelete, featureService, store.Server, store.SessionId, refreshCustomViews, getFilterCol, handleReloadProjectActivities, queryParams]);

  // Restore full state from Redux when returning to the tab (priority: Redux cache > Register > first view)
  useEffect(() => {
    if (!state.appKey || !dataStore[state.appKey]) return;
    const cached = dataStore[state.appKey];
    try {
      if (cached.pageView) {
        dataRef.current.pageView = cached.pageView;
        actions.setPageView(cached.pageView as PageView);
      }
      if (cached.selectedItem) {
        const parsed = typeof cached.selectedItem === 'string' ? JSON.parse(cached.selectedItem) : cached.selectedItem;
        dataRef.current.selectedItem = parsed;
        actions.setSelectedItem(parsed as FPAData);
        if (cached.selectedKeys) {
          const keys = typeof cached.selectedKeys === 'string' ? JSON.parse(cached.selectedKeys) : Array.from(cached.selectedKeys || []);
          dataRef.current.selectedKeys = new Set(keys);
          actions.setSelectedKeys(new Set(keys));
        }
      }
      if (cached.selectedRow) {
        const parsed = typeof cached.selectedRow === 'string' ? JSON.parse(cached.selectedRow) : cached.selectedRow;
        dataRef.current.selectedRow = parsed;
        actions.setSelectedDataRow(parsed as ProjectItem);
        fetchProjectLayout(parsed as ProjectItem);
      }
      if (cached.showMobileDetailView != null) {
        dataRef.current.showMobileDetailView = cached.showMobileDetailView;
        actions.setShowMobileDetailView(cached.showMobileDetailView);
      }
      if (cached.queryParams) {
        let parsed: QueryParams;
        try {
          parsed = typeof cached.queryParams === 'string' ? JSON.parse(cached.queryParams) : (cached.queryParams as QueryParams);
        } catch {
          parsed = { FILTER: { COL: [] } };
        }
        queryParams.current = parsed;
        dataReload(parsed);
      }
    } catch (error) {
      console.error('Error restoring project activities state from Redux:', error);
    }
  }, [state.appKey, dataStore.appInfos, actions, dataReload, fetchProjectLayout]);

  // Load persisted "last view" from Register on app changes
  useEffect(() => {
    lastViewIdFromRegisterRef.current = null;
    lastViewRegisterRowRef.current = null;
    setLastViewRegisterLoaded(false);
    resolvedInitialViewForAppRef.current = null;
    setInitialCustomViewId(undefined);

    if (!selectedApp) {
      setLastViewRegisterLoaded(true);
      return;
    }

    (async () => {
      const row = await listLastViewRegisterRow();
      if (row?.id) {
        lastViewRegisterRowRef.current = { id: row.id, assignTo: row.assignTo };
        lastViewIdFromRegisterRef.current = row.value != null ? String(row.value) : null;
      }
      setLastViewRegisterLoaded(true);
    })();
  }, [listLastViewRegisterRow, selectedApp]);

  // Resolve initial custom view selection after Register lookup and Custom Views load
  useEffect(() => {
    if (!selectedApp) return;
    if (!lastViewRegisterLoaded) return;
    if (isViewsLoading) return;
    if (resolvedInitialViewForAppRef.current === selectedApp) return;

    const cachedStateFilter = getFilterCol(queryParams.current).find(col => col.name === 'state');
    const cachedViewId = cachedStateFilter?.value ? String(cachedStateFilter.value) : null;

    if (cachedViewId) {
      resolvedInitialViewForAppRef.current = selectedApp;
      setInitialCustomViewId(undefined);
      return;
    }

    if (!customViews || customViews.length === 0) {
      resolvedInitialViewForAppRef.current = selectedApp;
      setInitialCustomViewId(undefined);
      return;
    }

    const registerViewIdRaw = lastViewIdFromRegisterRef.current;
    const registerViewId = registerViewIdRaw ? String(registerViewIdRaw).trim() : null;
    const registerExistsInViews = !!registerViewId && customViews.some(v => String(v.id ?? '') === registerViewId);

    const firstView = [...customViews].sort((a, b) => {
      const pa = Number(a.priority ?? Number.POSITIVE_INFINITY);
      const pb = Number(b.priority ?? Number.POSITIVE_INFINITY);
      if (Number.isFinite(pa) && Number.isFinite(pb) && pa !== pb) return pa - pb;
      if (Number.isFinite(pa) && !Number.isFinite(pb)) return -1;
      if (!Number.isFinite(pa) && Number.isFinite(pb)) return 1;
      return 0;
    })[0];
    const firstViewId = firstView?.id ? String(firstView.id) : null;
    const targetViewId = registerExistsInViews ? registerViewId! : firstViewId;

    if (targetViewId) {
      skipNextViewPersistRef.current = true;
      setInitialCustomViewId(targetViewId);
    } else {
      setInitialCustomViewId(undefined);
    }

    resolvedInitialViewForAppRef.current = selectedApp;
  }, [customViews, isViewsLoading, getFilterCol, lastViewRegisterLoaded, selectedApp, queryParams]);

  useEffect(() => {
    if (!pendingCreatedViewIdRef.current || isViewsLoading || !customViews) return;
    const pendingViewId = pendingCreatedViewIdRef.current;
    const newView = customViews.find(v => String(v.id ?? '') === pendingViewId);
    if (newView) {
      pendingCreatedViewIdRef.current = null;
      skipNextViewPersistRef.current = true;
      handleReloadProjectActivities(applyViewSelection(newView));
    } else {
      pendingViewRetryCountRef.current += 1;
      if (pendingViewRetryCountRef.current > 10) {
        console.warn(
          `Newly created view with ID ${pendingViewId} not found after ${pendingViewRetryCountRef.current} retries. View may not exist.`
        );
        pendingCreatedViewIdRef.current = null;
        pendingViewRetryCountRef.current = 0;
      }
    }
  }, [customViews, isViewsLoading, applyViewSelection, handleReloadProjectActivities]);

  // Apply layout orientation from CustomView when a view is selected
  useEffect(() => {
    const stateCol = getFilterCol(queryParams.current).find(col => col.name === 'state');
    const currentViewId = stateCol?.value != null ? String(stateCol.value) : null;

    if (currentViewId && currentViewId !== lastAppliedCustomViewIdRef.current) {
      const orientationInfo = getLayoutOrientationFromCustomView();
      if (orientationInfo) {
        let orientation: 'Detail' | 'Full' | undefined;
        if (isMobile) {
          orientation = orientationInfo.mobileOrientation || 'Full';
        } else if (deviceInfo.isTablet || deviceInfo.isIPad) {
          orientation = orientationInfo.tabletOrientation;
        } else {
          orientation = orientationInfo.desktopOrientation;
        }
        if (orientation === 'Detail' || orientation === 'Full') {
          const pageView: PageView = orientation === 'Detail' ? 'Grid' : 'Full';
          actions.setPageView(pageView);
          dataRef.current.pageView = pageView;
          lastAppliedCustomViewIdRef.current = currentViewId;
        } else {
          const defaultPageView: PageView = isMobile ? 'Full' : 'Grid';
          actions.setPageView(defaultPageView);
          dataRef.current.pageView = defaultPageView;
          lastAppliedCustomViewIdRef.current = currentViewId;
        }
      }
    } else if (!currentViewId && lastAppliedCustomViewIdRef.current) {
      lastAppliedCustomViewIdRef.current = null;
    }
  }, [
    customViews,
    isMobile,
    rowsData.length,
    getLayoutOrientationFromCustomView,
    actions,
    deviceInfo.isTablet,
    deviceInfo.isIPad,
    deviceInfo.orientation,
    getFilterCol,
    queryParams,
  ]);

  useEffect(() => {
    actions.setShowFilter(!isViewsLoading);
  }, [isViewsLoading, actions]);

  useKeyboardShortcuts([...addKey('c', { ctrl: true, shift: true }, () => onAddNew())], true);

  const getProjectStatusArray = useCallback(async () => {
    const statusArray: ProjectStatus[] = [];
    if (projectStatusMapping) {
      for (const key in projectStatusMapping as Record<string, { name: string; color: string }>) {
        const item = (projectStatusMapping as Record<string, { name: string; color: string }>)[key];
        statusArray.push({ id: key, name: item.name, color: item.color });
      }
    }
    statusArray.unshift({ id: allStatesLabel, name: allStatesLabel, color: 'black' });
    setProjectStatusList(statusArray);
  }, [allStatesLabel, projectStatusMapping]);

  // Main effect for selectedApp changes
  useEffect(() => {
    const initializeForNewApp = async () => {
      if (previousSelectedAppRef.current === selectedApp) return;
      previousSelectedAppRef.current = selectedApp;

      actions.setShowLoader(true);
      actions.setShowLoaderDetail(true);
      actions.setPageView('Grid');
      actions.setShowFilter(true);
      actions.setRowsData([]);
      actions.setSelectedKeys(undefined);
      actions.setSelectedDataRow({});
      actions.setDefinition(null);

      await getProjectStatusArray();
    };

    initializeForNewApp();
  }, [selectedApp, getProjectStatusArray, actions]);

  // Effect to reload data when all dependencies are ready
  useEffect(() => {
    if (selectedApp && projectStatusList.length > 0) {
      if (isViewsLoading || !lastViewRegisterLoaded) {
        return;
      }
      if (resolvedInitialViewForAppRef.current === selectedApp) {
        return;
      }
      const hasStateFilter = getFilterCol(queryParams.current).some(col => col.name === 'state' && String(col.value ?? '') !== '');
      if (hasStateFilter) {
        return;
      }
      const timeoutId = setTimeout(() => {
        listProjectList.reload();
      }, 100);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedApp, projectStatusList, isViewsLoading, lastViewRegisterLoaded, getFilterCol, queryParams]);

  // Keep dataRef in sync for persistence
  useEffect(() => {
    dataRef.current.pageView = pageView;
    dataRef.current.selectedItem = selectedItem ?? undefined;
    dataRef.current.selectedRow =
      selectedDataRow && Object.keys(selectedDataRow).length > 0 ? (selectedDataRow as ProjectItem) : undefined;
    dataRef.current.selectedKeys = selectedKeys ?? undefined;
    dataRef.current.showMobileDetailView = showMobileDetailView;
  }, [pageView, selectedItem, selectedDataRow, selectedKeys, showMobileDetailView]);

  // Mount: subscribe to CREATE_CONTEXT_APP. Unmount: persist full state to Redux and unsubscribe.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount/unmount only
  useEffect(() => {
    subscribe(AtollonEvent.CREATE_CONTEXT_APP, handleCreateContextApp);

    return () => {
      dispatch(
        updateAppInfo({
          key: state.appKey,
          data: {
            pageView: dataRef.current.pageView,
            queryParams: JSON.stringify(queryParams.current),
            selectedItem: dataRef.current.selectedItem != null ? JSON.stringify(dataRef.current.selectedItem) : undefined,
            selectedRow: dataRef.current.selectedRow != null ? JSON.stringify(dataRef.current.selectedRow) : undefined,
            selectedKeys: dataRef.current.selectedKeys != null && dataRef.current.selectedKeys.size > 0 ? JSON.stringify(Array.from(dataRef.current.selectedKeys)) : undefined,
            showMobileDetailView: dataRef.current.showMobileDetailView,
          },
        })
      );

      unsubscribe(AtollonEvent.CREATE_CONTEXT_APP, handleCreateContextApp);
    };
  }, []);

  const reloadProjectActivities = useCallback(
    (filterParams: QueryParams) => {
      stopLoadDataRef.current = false;
      dataReload(filterParams);
    },
    [dataReload]
  );

  const handleTableRowdblClick = (rowId: string | number): void => {
    invokeFinderApp(String(rowId));
  };

  const handleTableRowClick = (selection: Selection): void => {
    const keys = selection === 'all' ? undefined : (selection as Set<string | number>);
    actions.setSelectedKeys(keys);
    const key = keys?.size ? keys.values().next().value : undefined;
    const selectedRow = key ? rowsData.find((item) => item.id === key) : undefined;
    if (!selectedRow) {
      actions.setSelectedDataRow({});
      actions.setSelectedKeys(undefined);
      actions.setDefinition(null);
      return;
    }
    actions.setSelectedDataRow(selectedRow);
    fetchProjectLayout(selectedRow);
  };

  const handleListRowClick = (selection: Selection): void => {
    const keys = selection === 'all' ? undefined : (selection as Set<string | number>);
    const key = keys?.size ? keys.values().next().value : undefined;
    if (key && selectedKeys?.has(key)) {
      return;
    }
    if (key) {
      actions.setShowLoaderDetail(true);
      actions.setSelectedKeys(keys);
      dataRef.current.pageView = 'Full';
      actions.setPageView('Full');
      const selectedRow = rowsData.find((item) => item.id === key);

      if (!selectedRow) {
        actions.setSelectedDataRow({});
        actions.setSelectedKeys(undefined);
        actions.setDefinition(null);
        return;
      }
      actions.setSelectedDataRow(selectedRow);
      fetchProjectLayout(selectedRow);
    }
  };
  const scrollPositionRef = useRef<number>(0);
  const handleRowClickMobile = (selection: Selection): void => {
    const listContainer = document.getElementById('listProjectCardsMobile');
    if (listContainer) {
      scrollPositionRef.current = listContainer.scrollTop;
    }
    const nameSearchExists = getFilterCol(queryParams.current).find((col: FilterColumn) => col.name === 'nameSearch');
    if (nameSearchExists) {
      searchedValueRef.current = typeof nameSearchExists.value === 'string' ? nameSearchExists.value : '';
    }
    const keys = selection === 'all' ? undefined : (selection as Set<string | number>);
    const key = keys?.size ? keys.values().next().value : undefined;
    actions.setSelectedKeys(undefined);
    const selectedRow = key ? rowsData.find((item) => item.id === key) : undefined;
    if (!selectedRow) {
      actions.setSelectedDataRow({});
      actions.setSelectedKeys(undefined);
      actions.setDefinition(null);
      return;
    }
    actions.setSelectedDataRow(selectedRow);
    fetchProjectLayout(selectedRow);
    actions.setShowMobileDetailView(true);
  };

  const handleViewChange = (view: string) => {
    const pageView = view as PageView;
    actions.setPageView(pageView);
    dataRef.current.pageView = pageView;
  };

  const getStatusColor = (statusName: string) => {
    const statusObj = projectStatusList.find((obj) => obj.name === statusName);
    return (
      <>
        <CircleFill style={{ paddingRight: '10px' }} size={'18px'} color={statusObj?.color} /> {statusName}
      </>
    );
  };

  const addStatusColor = (statusName: string) => {
    const statusObj = projectStatusList.find((obj) => obj.name === statusName);
    return statusObj?.color;
  };

  const showEmptyViews = useMemo(
    () => !isViewsLoading && (customViews?.length ?? 0) === 0,
    [customViews?.length, isViewsLoading]
  );

  const EmptyViewsCTA = useMemo(
    () => (
      <Flex width="100%" height="100%" justifyContent="center" alignItems="center">
        <AtollonButton designType={AtollonButtonDesignType.PRIMARY} onPress={handleOpenCreateViewDialog}>
          Add View
        </AtollonButton>
      </Flex>
    ),
    [handleOpenCreateViewDialog]
  );

  const NoResultsMessage = () => (
    <Flex direction="row" width="100%" alignContent="center" justifyContent="center">
      <IllustratedMessage>
        <NotFound />
        <Heading>{t('no_results', { ns: 'layout_components' })}</Heading>
        <Content>{t('try_another_search', { ns: 'layout_components' })}</Content>
      </IllustratedMessage>
    </Flex>
  );

  const getTranslatedColumnName = useCallback((column: ColumnDefinition) => {
    if (String(column?.name ?? '').trim() === 'Client') {
      return t('client', { ns: 'layout_components', defaultValue: 'Client' });
    }
    return column.name;
  }, [t]);

  const getHeight = (baseHeight: number) => {
    return `${baseHeight - 120}px`;
  };

  const getWidth = () => {
    return `95%`;
  };

  const detailStyle = {
    height: getHeight(screenSize.height - 20),
  };

  const cardStyle = {
    height: getHeight(screenSize.height),
    width: getWidth(),
    paddingBottom: '15px',
  };

  const filterStyle = {
    height: getHeight(screenSize.height + 60),
    overflow: 'hidden auto',
  };

  const onAddNew = () => {
    modeRef.current = 'new';
    actions.setShowAdd(true);
  };

  const nAddNewClose = (reload_flag: boolean) => {
    if (reload_flag) listProjectList.reload();

    actions.setShowAdd(false);
  };
  const nAddNewCloseDismiss = () => {
    actions.setShowAdd(false);
    listProjectList.reload();
  };

  const handleCopy = () => {
    if (selectedItem) {
      modeRef.current = 'copy';
      actions.setShowAdd(true);
    } else {
      ToastQueue.negative(t('no_selected_item', { ns: 'layout_components' }), { timeout: 3000 });
    }
  };

  const handleEdit = () => {
    if (selectedItem?.id) {
      invokeFinderApp(String(selectedItem.id));
    } else {
      ToastQueue.negative(t('no_selected_item', { ns: 'layout_components' }), { timeout: 3000 });
    }
  };

  const handleDelete = (item: FPAData | null) => {
    if (item === null) return;
    actions.setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (selectedItem === null) return;
    actions.setIsDeleteDialogOpen(false);
    if (selectedItem.type === FPADataTypes.PROJECT) {
      const res = await projectService.deleteProject(new DeleteProjectRequest(store.Server, store.SessionId, selectedItem.id));
      if (res.EXCEPTION) {
        ToastQueue.negative(res.EXCEPTION.message, { timeout: 3000 });
      } else {
        listProjectList.reload();
      }
    }
  };

  const handleDeleteProject = () => {
    if (selectedItem === null) return;
    handleDelete(selectedItem);
  };

  const handleMoveClose = (moved: boolean) => {
    if (moved) {
      listProjectList.reload();
    }
    actions.setIsMoveDialogOpen(false);
  };
  const handleMove = (_selectedItem: FPAData | null) => {
    if (_selectedItem) {
      actions.setIsMoveDialogOpen(true);
      actions.setSelectedItem(_selectedItem);
    } else {
      ToastQueue.negative(t('no_selected_item', { ns: 'layout_components' }), { timeout: 3000 });
    }
  };
  const handleCreateContextApp = (event: AtollonEvent) => {
    if (event.detail.isCopy) {
      handleCopy();
    } else {
      handleMove(event.detail.selectedItem);
    }
  };

  const scrollToSelected = useCallback(() => {
      setTimeout(() => {
        const listContainer = document.getElementById('listProjectCardsMobile');
        if (listContainer && scrollPositionRef.current > 0) {
          listContainer.scrollTo({
            top: scrollPositionRef.current,
            behavior: 'smooth',
          });
          //console.log('Restored scroll position:', scrollPositionRef.current);
        }
      }, 1000);
    }, []);
  
    const systemAttributes = {
      onPrint: (_item: FPAData | null) => {},
      onEdit: (_item: FPAData | null) => {},
      onDelete: handleDelete,
      onChat: (_item: FPAData | null) => {},
      onConfig: (_item: FPAData | null, _type: string) => {},
    };

    const handleMobileDetailClose = () => {
      stopLoadDataRef.current = true;
      actions.setShowMobileDetailView(false);
      dataRef.current.showMobileDetailView = false;
      actions.setSelectedKeys(undefined);
      dataRef.current.selectedItem = undefined;
      dataRef.current.selectedRow = undefined;
      dataRef.current.selectedKeys = undefined;
      actions.setSelectedDataRow({});
      actions.setSelectedItem(null);
      actions.setDefinition(null);
      scrollToSelected();
    };

    const handleOpenFinder = () => {
      if (selectedItem?.id) {
        invokeFinderApp(String(selectedItem.id));
      }
      stopLoadDataRef.current = true;
      actions.setShowMobileDetailView(false);
      dataRef.current.showMobileDetailView = false;
    };

    return (
      <div key={0}>
        <ProjectActivitiesLayout
          showMobileDetailView={showMobileDetailView}
          mobileListView={
            <MobileListView
              showEmptyViews={showEmptyViews}
              filterParams={queryParams.current}
              onFilterChange={handleReloadProjectActivities}
              onSearchChange={handleReloadProjectActivities}
              searchedText={searchedValueRef.current}
              fpaTypeCategory={projectListKey.current}
              items={listProjectList.items}
              rowsData={rowsData}
              projectStatusList={projectStatusList}
              selectedKeys={selectedKeys}
              onRowClick={handleRowClickMobile}
              onAddNew={onAddNew}
              isLoading={listProjectList.isLoading}
              loadMore={listProjectList.loadMore}
              deviceInfo={deviceInfo}
              listBoxRef={listBoxRef}
              customViews={customViews}
              hasMoreCustomViews={hasMoreCustomViews}
              onLoadMoreCustomViews={loadMoreCustomViews}
              onRefreshCustomViews={refreshCustomViews}
              formSettings={formSettings}
              initialViewId={initialCustomViewId}
              onCreateView={handleOpenCreateViewDialog}
              onEditView={handleEditView}
              onDuplicateView={handleDuplicateView}
              onDeleteViewClick={handleDeleteViewClick}
              resetBaselineRef={resetViewBaselineRef}
            />
          }
          mobileDetailView={
            <MobileDetailView
              selectedItem={selectedItem}
              selectedDataRow={selectedDataRow}
              definition={definition}
              showLoader={showLoaderDetail}
              onClose={handleMobileDetailClose}
              onOpenFinder={handleOpenFinder}
              systemAttributes={systemAttributes}
              addStatusColor={addStatusColor}
            />
          }
          desktopLayout={
          // Desktop/Tablet Layout
          <Grid
            areas={
              (deviceInfo.isTablet || deviceInfo.isIPad)
                ? pageView === 'Full'
                  ? ['header header', 'filter filter', 'card detail']
                  : ['header header', 'filter filter', 'detail detail']
                : pageView === 'Full'
                  ? ['filter header header', 'filter card detail']
                  : ['filter header header', 'filter detail detail']
            }
            columns={{
              base: ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape')
                ? ['35%', '65%']
                : ['245px', '28%', 'auto'],
              L: ((deviceInfo.isTablet || deviceInfo.isIPad) && deviceInfo.orientation === 'landscape')
                ? ['35%', '65%']
                : ['245px', '28%', 'auto'],
            }}
            rows={
              (deviceInfo.isTablet || deviceInfo.isIPad)
                ? ['auto', 'auto', '1fr']
                : ['auto', '1fr']
            }
            height="100%"
            width="100%"
            UNSAFE_style={{ backgroundColor: '#f5f6f9', padding: '0px', margin: '0px', overflow: 'hidden' }}
          >
            {/* Filter Area - Hidden on tablet landscape, shown for desktop */}
            {!(deviceInfo.isTablet || deviceInfo.isIPad) && (
              <View gridArea="filter">
                {showFilter ? (
                  <FilterComponent
                    key={selectedApp || 'default'}
                    showEmptyViews={showEmptyViews}
                    onFilterChange={handleReloadProjectActivities}
                    filterParams={queryParams.current}
                    filterStyle={filterStyle}
                    customViews={customViews}
                    hasMoreCustomViews={hasMoreCustomViews}
                    onLoadMoreCustomViews={loadMoreCustomViews}
                    onRefreshCustomViews={refreshCustomViews}
                    formSettings={formSettings}
                    fpaTypeCategory={projectListKey.current}
                    initialViewId={initialCustomViewId}
                    onCreateView={handleOpenCreateViewDialog}
                    onEditView={handleEditView}
                    onDuplicateView={handleDuplicateView}
                    onDeleteViewClick={handleDeleteViewClick}
                    resetBaselineRef={resetViewBaselineRef}
                    deviceInfo={deviceInfo}
                  />
                ) : (
                  <Flex
                    height={'100%'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    UNSAFE_style={{ backgroundColor: 'white' }}
                  >
                    <Loader />
                  </Flex>
                )}
              </View>
            )}

            {/* Header Area */}
            <View width={'100%'} gridArea="header" UNSAFE_style={pageView === 'Full' ? {} : { height: '100%' }}>
              <HeaderComponent
                onPageViewChange={handleViewChange}
                autoChanged={pageView}
                filterParams={queryParams.current}
                onSearchChange={handleReloadProjectActivities}
                fpaTypeCategory={projectListKey.current}
                onAddNew={onAddNew}
                onCopy={handleCopy}
                onDelete={handleDeleteProject}
                onEdit={handleEdit}
              />
            </View>
  
            {/* Filter Area - Shown for tablets/iPads under header */}
            {(deviceInfo.isTablet || deviceInfo.isIPad) && (
              <View gridArea="filter" UNSAFE_style={{ height: '60px', backgroundColor: 'white', borderBottom: '1px solid #e0e0e0' }}>
                <FilterComponent
                  key={selectedApp || 'default'}
                  showEmptyViews={showEmptyViews}
                  onFilterChange={handleReloadProjectActivities}
                  filterParams={queryParams.current}
                  customViews={customViews}
                  hasMoreCustomViews={hasMoreCustomViews}
                  onLoadMoreCustomViews={loadMoreCustomViews}
                  onRefreshCustomViews={refreshCustomViews}
                  formSettings={formSettings}
                  fpaTypeCategory={projectListKey.current}
                  initialViewId={initialCustomViewId}
                  onCreateView={handleOpenCreateViewDialog}
                  onEditView={handleEditView}
                  onDuplicateView={handleDuplicateView}
                  onDeleteViewClick={handleDeleteViewClick}
                  resetBaselineRef={resetViewBaselineRef}
                  deviceInfo={deviceInfo}
                />
              </View>
            )}
  
            {pageView === 'Full' ? (
              <>
                {!showEmptyViews && showLoader ? (
                  <Flex
                    width={'100%'}
                    height={'100%'}
                    position={'absolute'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    UNSAFE_style={{ backgroundColor: 'white' }}
                  >
                    <Loader />
                  </Flex>
                ) : (
                  <>
                    {/* Card List Area */}
                    <View gridArea="card" UNSAFE_style={{ backgroundColor: '#f5f6f9' }}>
                      {showEmptyViews ? null : rowsData?.length > 0 ? (
                        <ListBox
                          key={activeViewId ?? 'default'}
                          autoFocus
                          id="listProjectCards"
                          selectionMode="single"
                          onSelectionChange={handleListRowClick}
                          marginStart={'15px'}
                          marginTop={'13px'}
                          minHeight={'130px'}
                          maxHeight={'100vh'}
                          selectedKeys={selectedKeys ?? new Set()}
                          aria-label="Project Activities Cards"
                          items={listProjectList.items}
                          isLoading={listProjectList.isLoading}
                          onLoadMore={listProjectList.loadMore}
                          UNSAFE_style={cardStyle}
                        >
                          {item => (
                            <Item key={item.id} textValue={item.name}>
                              <CardComponent item={item} projectStatusList={projectStatusList} />
                            </Item>
                          )}
                        </ListBox>
                      ) : (
                        <View marginTop={'13px'}>
                          <NoResultsMessage />
                        </View>
                      )}
                    </View>
  
                    {/* Detail Area */}
                    <View
                      gridArea="detail"
                      backgroundColor={'static-white'}
                      borderRadius={'large'}
                      padding={'size-100'}
                      paddingTop={'0'}
                      marginTop={'17px'}
                      width={'98%'}
                      UNSAFE_style={detailStyle}
                      UNSAFE_className={styles.detailView}
                    >
                      {showEmptyViews ? (
                        EmptyViewsCTA
                      ) : definition ? (
                        showLoaderDetail ? (
                          <Loader />
                        ) : (
                          <View height="100%" UNSAFE_className={styles.detailsHolder} UNSAFE_style={{ overflow: 'hidden auto' }} marginStart={'10px'}>
                            {selectedItem && rowsData?.length > 0 && (
                              <>
                                <View UNSAFE_className={styles.service_status_bar} UNSAFE_style={{ backgroundColor: addStatusColor(selectedDataRow.customStateName ?? '') }} />
                                <View height={'2px'}>&nbsp;</View>
                                <ComponentFactory
                                  selectedItem={selectedItem}
                                  definition={definition}
                                  systemAttributes={systemAttributes}
                                />
                              </>
                            )}
                          </View>
                        )
                      ) : null}
                    </View>
                  </>
                )}
              </>
            ) : (
              // Table View for Detail-only mode
              <View gridArea="detail">
                {showEmptyViews ? (
                  EmptyViewsCTA
                ) : showLoader ? (
                  <Loader />
                ) : (
                  <View position={'relative'} overflow={'auto'} height={'100%'} width={'100%'} UNSAFE_className={styles.service_activities}>
                    {(visibleGridColumns?.length > 0 || columnsDataRef?.current?.length > 0) && rowsData?.length > 0 ? (
                      <TableView
                        key={activeViewId ?? 'default'}
                        UNSAFE_className="tblLayoutComponent"
                        aria-label="Project Activities"
                        onSelectionChange={handleTableRowClick}
                        selectionMode="single"
                        selectedKeys={selectedKeys ?? new Set()}
                        selectionStyle="highlight"
                        width={{ base: '100%', L: '100%', M: '1100px' }}
                        minHeight={'200px'}
                        maxHeight={{ base: '100vh', L: '625px' }}
                        onAction={handleTableRowdblClick}
                      >
                        <TableHeader columns={visibleGridColumns.length > 0 ? visibleGridColumns : columnsDataRef.current}>
                          {(column: ColumnDefinition) => (
                            <Column showDivider key={column.key}>
                              {getTranslatedColumnName(column)}
                            </Column>
                          )}
                        </TableHeader>
                        <TableBody items={listProjectList.items} loadingState={listProjectList.loadingState} onLoadMore={listProjectList.loadMore}>
                          {(item: ProjectItem) => (
                            <Row>
                              {columnKey => (
                                <Cell>
                                  {(() => {
                                    const value = item[columnKey];
                                    if (moment(value as moment.MomentInput, moment.ISO_8601, true).isValid()) {
                                      return moment(value as moment.MomentInput).format('MM/DD/YYYY | hh:mm A');
                                    } else if (columnKey === 'customStateName') {
                                      return getStatusColor(String(value ?? ''));
                                    } else {
                                      return (value ?? '') as ReactNode;
                                    }
                                  })()}
                                </Cell>
                              )}
                            </Row>
                          )}
                        </TableBody>
                      </TableView>
                    ) : (
                      <NoResultsMessage />
                    )}
                  </View>
                )}
              </View>
            )}
          </Grid>
          }
        />

        {/* Dialog Components */}
        <DataProvider>
          <DialogContainer onDismiss={nAddNewCloseDismiss}>
            {showAdd && (
              <ContextNewDialog
                parentItem={selectedItem}
                onClose={nAddNewClose}
                additionalMode={true}
                additionalData={{ fpaTypeCategory: projectListKey.current, fpaType: 'PROJECT', haveMasterActivity: false }}
                mode={modeRef.current}
              />
            )}
            {isMoveDialogOpen && (
              <ContextMoveDialog
                onClose={handleMoveClose}
                parentItem={null}
                selectedItem={selectedItem}
                showActivities={false}
                showProjects={true}
                showFolders={true}
                relatedToActivityType={selectedItem?.item_type_id}
                selectedItemType={FPADataTypes.FOLDER}
              />
            )}
          </DialogContainer>
        </DataProvider>
        
        <DeleteConfirmationDialog 
          isOpen={isDeleteDialogOpen} 
          onClose={() => actions.setIsDeleteDialogOpen(false)} 
          onConfirm={handleConfirmDelete} 
          itemName={selectedItem?.title} 
        />

        <DialogContainer isDismissable={false} onDismiss={handleCloseCustomViewDialog}>
          {(isCreateViewDialogOpen || customViewDialogEditView != null) && (
            <CustomViewDialog
              formSettings={formSettings || []}
              applicationId={selectedApp || ''}
              onCancel={handleCloseCustomViewDialog}
              onSave={handleCreateViewSaved}
              fpaTypeCategory={projectListKey.current}
              editView={customViewDialogEditView ?? undefined}
              isDuplicate={customViewDialogIsDuplicate}
            />
          )}
        </DialogContainer>

        <DialogContainer onDismiss={() => setViewToDelete(null)}>
          {viewToDelete && (
            <AlertDialog
              title={t('delete_view', { ns: 'layout_components', defaultValue: 'Delete View' })}
              variant="destructive"
              primaryActionLabel={t('delete', { ns: 'layout_components', defaultValue: 'Delete' })}
              cancelLabel={t('cancel', { ns: 'layout_components', defaultValue: 'Cancel' })}
              onPrimaryAction={handleDeleteViewConfirm}
              onCancel={() => setViewToDelete(null)}
              isPrimaryActionDisabled={isDeletingView}
            >
              {t('delete_view_confirmation', {
                ns: 'layout_components',
                defaultValue: 'Are you sure you want to delete this view? This action cannot be undone.',
              })}
            </AlertDialog>
          )}
        </DialogContainer>
      </div>
    );
}
